/**
 * @fileoverview Better Auth client `fetchOptions.onError` — policy + logging.
 *
 * @purpose Keeps `client.ts` thin and makes onError logic unit-testable (CRAP / coverage).
 * @usage Used only from `client.ts` (`createAuthClient` fetchOptions).
 * @ownership Core infrastructure — authentication client
 * @dependencies ./extract-error-info
 * @restrictions Do not log secrets; browser-safe.
 *
 * @see packages/core/src/lib/better-auth/client.ts
 */

import { extractErrorInfo } from "./extract-error-info";

export type BetterAuthClientErrorLogEnv = {
  NODE_ENV?: string;
  DEBUG_AUTH?: string;
}

/**
 * When true, fetch errors are not logged (production unless DEBUG_AUTH=true).
 */
export function shouldSuppressBetterAuthClientFetchErrorLog(
  env: BetterAuthClientErrorLogEnv,
): boolean {
  return env.NODE_ENV === "production" && env.DEBUG_AUTH !== "true";
}

export type BetterAuthFetchOnErrorLogger = Pick<Console, "error">;

/**
 * Logs a single fetch error after suppress policy was already applied.
 */
export function logBetterAuthClientFetchError(
  error: unknown,
  log: BetterAuthFetchOnErrorLogger = console,
): void {
  if (error === null) {
    log.error("[Better Auth Client] Request error: null error received");
    return;
  }
  if (error === undefined) {
    log.error("[Better Auth Client] Request error: undefined error received");
    return;
  }
  log.error("[Better Auth Client] Request error:", extractErrorInfo(error));
}

/**
 * `createAuthClient` fetch `onError` handler: env-based suppress, then log.
 */
export function betterAuthClientFetchOnError(error: unknown): void {
  if (
    shouldSuppressBetterAuthClientFetchErrorLog({
      NODE_ENV: process.env.NODE_ENV,
      DEBUG_AUTH: process.env.DEBUG_AUTH,
    })
  ) {
    return;
  }
  logBetterAuthClientFetchError(error);
}
